/**
 * Common options for motion plans
 */

import * as zod from 'zod';

import {
  ABSOLUTE_MAX_JOINT_ACCELERATIONS,
  ABSOLUTE_MAX_JOINT_SPEEDS,
  ABSOLUTE_MAX_TOOLTIP_SPEED,
  ArmJointAccelerations,
  ArmJointVelocities,
  DEFAULT_BASE_ACCELERATION_SCALING,
  DEFAULT_BASE_VELOCITY_SCALING,
  DEFAULT_MAX_JOINT_ACCELERATIONS,
  DEFAULT_MAX_JOINT_SPEEDS,
  DEFAULT_MAX_TOOLTIP_SPEED,
  DEFAULT_SCALING_FACTOR,
  TESTRUN_MAX_JOINT_SPEEDS,
} from '@sb/motion-planning';

export const SpeedProfile = zod.object({
  maxJointSpeeds: ArmJointVelocities,
  // Maximum tooltip velocity in meters per second
  maxTooltipSpeed: zod.number(),
  // Maximum tooltip acceleration in meters per second squared
  // This is optional because it is not used in the ROS2 motion planner
  maxTooltipAcceleration: zod.number().nullable().default(null),
  maxJointAccelerations: ArmJointAccelerations,
  // ROS2 uses scaling factors as a percent of the absolute max
  // instead raw acceleration / velocity values
  // percentage of the absolute max acceleration and velocity
  baseAccelerationScaling: zod
    .number()
    .default(DEFAULT_BASE_ACCELERATION_SCALING),
  baseVelocityScaling: zod.number().default(DEFAULT_BASE_VELOCITY_SCALING),
  // percentage of the base acceleration and velocity scaling
  scalingFactor: zod.number().default(DEFAULT_SCALING_FACTOR),
});

export type SpeedProfile = zod.infer<typeof SpeedProfile>;

export const ABSOLUTE_MAX_SPEED_PROFILE = {
  maxJointSpeeds: ABSOLUTE_MAX_JOINT_SPEEDS,
  maxJointAccelerations: ABSOLUTE_MAX_JOINT_ACCELERATIONS,
  maxTooltipSpeed: ABSOLUTE_MAX_TOOLTIP_SPEED,
  maxTooltipAcceleration: null,
  baseAccelerationScaling: 1,
  baseVelocityScaling: 1,
  scalingFactor: 1,
};

export const DEFAULT_SPEED_PROFILE = {
  maxJointSpeeds: DEFAULT_MAX_JOINT_SPEEDS,
  maxJointAccelerations: DEFAULT_MAX_JOINT_ACCELERATIONS,
  maxTooltipSpeed: DEFAULT_MAX_TOOLTIP_SPEED,
  maxTooltipAcceleration: null,
  baseAccelerationScaling: 1,
  baseVelocityScaling: 1,
  scalingFactor: 1,
};

export const TESTRUN_SPEED_PROFILE = {
  maxJointSpeeds: TESTRUN_MAX_JOINT_SPEEDS,
  maxJointAccelerations: DEFAULT_MAX_JOINT_ACCELERATIONS,
  maxTooltipSpeed: DEFAULT_MAX_TOOLTIP_SPEED,
  maxTooltipAcceleration: null,
  baseAccelerationScaling: 1,
  baseVelocityScaling: 1,
  scalingFactor: 0.5,
};
